import { createImageBlock } from "../domain/blocks/image";
import {
  clampImageHeight,
  defaultImageHeightPx,
} from "../domain/image-height";
import { defaultWidthPxForImageBlock } from "../domain/facility-logo-sizing";
import {
  effectiveImageBoxWidthPx,
  storedImageWidthPx,
} from "../domain/image-width";
import { isFacilityLogoMediaRef } from "../domain/media";
import { wrapMergeTag } from "../domain/merge-tags";
import type { ImageBlock } from "../domain/types";

export type ImageBlockMediaAction = "add" | "replace" | "remove";

type ImageBlockMediaPatch = Pick<ImageBlock, "media" | "intrinsicSize">;

type ImageBlockMediaChangeArgs = {
  block: ImageBlock;
  action: ImageBlockMediaAction;
  contentMaxWidth: number;
  fillColumnAllowed?: boolean;
  mediaPatch?: ImageBlockMediaPatch;
  alt?: string;
};

export function resolveImageBlockAlt(
  media: ImageBlock["media"],
  alt: string | undefined,
): string {
  const trimmed = (alt ?? "").trim();
  if (trimmed.length > 0) {
    return trimmed;
  }
  if (isFacilityLogoMediaRef(media)) {
    return wrapMergeTag("facility.long_name");
  }
  return "";
}

function resolveAddWidthPx(
  block: ImageBlock,
  intrinsicSize: ImageBlock["intrinsicSize"],
  contentMaxWidth: number,
): number | null {
  if (intrinsicSize === null) {
    return null;
  }
  return defaultWidthPxForImageBlock(block, intrinsicSize, contentMaxWidth);
}

function resolveReplaceWidthPx(
  block: ImageBlock,
  intrinsicSize: ImageBlock["intrinsicSize"],
  contentMaxWidth: number,
): number | null {
  const stored = storedImageWidthPx(block);
  if (stored !== null) {
    return stored;
  }
  return resolveAddWidthPx(block, intrinsicSize, contentMaxWidth);
}

function resolveReplaceHeightPatch(
  block: ImageBlock,
  mediaPatch: Pick<ImageBlock, "intrinsicSize">,
  contentMaxWidth: number,
  fillColumnAllowed: boolean,
): Partial<Pick<ImageBlock, "heightMode" | "heightPx" | "objectFit">> {
  if (block.heightMode === "auto") {
    return {};
  }

  if (block.heightMode === "fillColumn") {
    if (fillColumnAllowed) {
      return {};
    }
    return {
      heightMode: "auto",
      heightPx: null,
      objectFit: createImageBlock().objectFit,
    };
  }

  const nextBlock: ImageBlock = { ...block, ...mediaPatch };
  const naturalSize =
    mediaPatch.intrinsicSize !== null
      ? {
          width: mediaPatch.intrinsicSize.width,
          height: mediaPatch.intrinsicSize.height,
        }
      : null;

  if (
    naturalSize &&
    naturalSize.width > 0 &&
    contentMaxWidth > 0
  ) {
    const boxWidth = effectiveImageBoxWidthPx(nextBlock, contentMaxWidth);
    return {
      heightMode: "fixed",
      heightPx: clampImageHeight(
        Math.round((boxWidth * naturalSize.height) / naturalSize.width),
      ),
    };
  }

  return {
    heightMode: "fixed",
    heightPx: defaultImageHeightPx(block),
  };
}

export function imageBlockPatchForMediaChange(
  args: ImageBlockMediaChangeArgs,
): Partial<ImageBlock> {
  const { block, action, contentMaxWidth, fillColumnAllowed = false } = args;
  const defaultObjectFit = createImageBlock().objectFit;

  if (action === "remove") {
    return {
      media: null,
      intrinsicSize: null,
      alt: "",
      widthPx: null,
      heightMode: "auto",
      heightPx: null,
      objectFit: defaultObjectFit,
    };
  }

  if (args.mediaPatch === undefined) {
    return {};
  }

  const mediaPatch = args.mediaPatch;
  const alt = resolveImageBlockAlt(mediaPatch.media, args.alt);
  const widthPx =
    action === "add"
      ? resolveAddWidthPx(block, mediaPatch.intrinsicSize, contentMaxWidth)
      : resolveReplaceWidthPx(
          block,
          mediaPatch.intrinsicSize,
          contentMaxWidth,
        );

  if (action === "add") {
    return {
      ...mediaPatch,
      alt,
      widthPx,
      heightMode: "auto",
      heightPx: null,
      objectFit: defaultObjectFit,
    };
  }

  return {
    ...mediaPatch,
    alt,
    widthPx,
    ...resolveReplaceHeightPatch(
      block,
      mediaPatch,
      contentMaxWidth,
      fillColumnAllowed,
    ),
  };
}

export { isImageDefaultWidth, resolveImageWidthCommit } from "../domain/image-width";
